import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../../Helpers/Handler";
import { currencyFormatter, formatDateTime, stripHtml } from "../../../Helpers/Utils";
import ListLayout from "../../../Components/List";
import PlanFormModal from "./PlanFormModal";
import useListParams from "../../../Helpers/useListParams";

const INITIAL_FILTERS = { search: "", searchKey: "name", count: 10, status: "" };
const SEARCH_KEYS = [{ key: "name", value: "Name / description / trip protection ID" }];

const TripProtection = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const filterItems = {
        dropdown: [
            {
                key: "status",
                name: "Status",
                options: [
                    { key: "", name: "All" },
                    { key: "true", name: "Active" },
                    { key: "false", name: "Inactive" },
                ],
            },
        ],
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/admin/booking/protection/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.status !== "" ? { status: filters.status === "true" } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.status]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const openCreate = () => setShowForm(true);
    const closeForm = () => setShowForm(false);
    const onSaved = () => { setShowForm(false); load(); };

    const columns = [
        { key: "tpId", name: "Trip Protection ID", mandatory: true },
        { key: "name", name: "Name", mandatory: true },
        { key: "description", name: "Description", render: (row) => <span className="text-truncate d-inline-block" style={{ maxWidth: 320 }}>{stripHtml(row.description)}</span> },
        { key: "percentage", name: "Percentage", render: (row) => `${row.percentage}%` },
        { key: "amount", name: "Amount", render: (row) => currencyFormatter(row.amount || 0) },
        {
            key: "status",
            name: "Status",
            render: (row) => <span className={`badge rounded-pill ${row.status ? "text-bg-success" : "text-bg-secondary"}`}>{row.status ? "Active" : "Inactive"}</span>,
        },
        { key: "createdAt", name: "Created", render: (row) => formatDateTime(row.createdAt) },
    ];

    return (
        <>
            <ListLayout
                title="Trip Protection"
                description="Trip protection plans offered at booking - at least one must be enabled, or quoting a booking fails."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by name, description, or trip protection ID..."
                onRowClick={(row) => navigate(`/protection/${row.tpId}`)}
                addList={openCreate}
            />

            {showForm ? <PlanFormModal plan={null} onClose={closeForm} onSaved={onSaved} /> : ""}
        </>
    );
};

export default TripProtection;
